'use client'

import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { getUpdatedParamValue } from 'shared/libs/query/query'
import { AllParamsKeys } from 'shared/types/QueryModel'

import { useCallback } from 'react'

export function useAppSearchParams() {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()!
  const params: Partial<Record<AllParamsKeys, any>> = {}

  searchParams.forEach((value, key) => {
    params[key as AllParamsKeys] = value
  })

  const getParam = (key: AllParamsKeys) => {
    return searchParams.get(key)
  }

  const setParam = (key: AllParamsKeys, value: string) => {
    router.push(pathname + '?' + createQueryString(key, String(value)), {
      scroll: false,
    })
  }

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams)
      if (value === 'false' || !Boolean(value)) {
        params.delete(name)
      } else {
        params.set(name, value)
      }
      return params.toString()
    },
    [searchParams],
  )

  const resetParams = () => {
    router.push(pathname, {
      scroll: false,
    })
  }

  const removeParam = (key: AllParamsKeys) => {
    const params = new URLSearchParams(window.location.search)
    params.delete(key)

    router.push(pathname + (params.toString() ? `?${params.toString()}` : ''), {
      scroll: false,
    })
  }

  const toggleParam = (key: AllParamsKeys, value: string) => {
    const currentValue = searchParams.get(key)
    const newValue = getUpdatedParamValue(currentValue, value)

    router.push(pathname + '?' + createQueryString(key, newValue), {
      scroll: false,
    })
  }

  const updateParams = (
    updates: Partial<Record<AllParamsKeys, string | null>>,
  ) => {
    const params = new URLSearchParams(searchParams)

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null) {
        params.delete(key)
      } else {
        params.set(key, value)
      }
    })

    router.push(pathname + (params.toString() ? `?${params.toString()}` : ''), {
      scroll: false,
    })
  }

  return {
    setParam,
    getParam,
    updateParams,
    params,
    toggleParam,
    removeParam,
    resetParams,
  }
}
